import { call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import SuperFetch from 'library/helpers/superFetch';
import { Notification } from 'zComponents/atoms';
import { accessActions } from './slice';
import { peopleActions } from 'reduxStore/people/slice';
import { teamsActions } from 'reduxStore/teams/slice';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { assetGroupActions } from 'reduxStore/assetGroups/slice';
import { fetchOrgAccess } from './expandCompactAccess';
import { coalesceAccessRefresh } from './accessRefresh';

// The IR PrimaryPermissionGroup is served on its OWN endpoint (kept out of the legacy /access
// query). Fetch it separately and merge under `primary` — best-effort: a failure here must not
// break the ordinary permission load. Org-level (no siteID — the primary is site-invariant).
function* fetchPrimaryPermissionGroups() {
	try {
		const res = yield call(() => SuperFetch.get('/access/primary-permission-groups'));
		if (res.statusCode === 200) return res.data || [];
	} catch {
		// best-effort — ordinary access still renders
	}
	return [];
}

// ── Get all access permissions (single API call) ─────────────────────────────
export function* getAccessPermissions() {
	const { orgID, siteID: profileSiteID } = yield select((state) => state.Auth.selectedProfile);
	const siteID = profileSiteID;
	const query = siteID !== 'all' ? { siteID } : {};

	yield put(accessActions.getAccessPermissionsStart({ orgID, siteID }));

	try {
		const response = yield call(fetchOrgAccess, SuperFetch, query);
		if (response.statusCode === 200) {
			const primary = yield call(fetchPrimaryPermissionGroups);
			yield put(
				accessActions.getAccessPermissionsSuccess({ orgID, siteID, data: { ...response.data, primary } })
			);
		} else {
			throw new Error(response.error?.message || 'Failed to load access permissions.');
		}
	} catch (error) {
		yield put(accessActions.getAccessPermissionsError({ orgID, siteID, error: error.message }));
		Notification('error', 'Load failed', 'Failed to load access permissions.');
	}
}

// ── Silent refresh — no loading flag, no error notification ──────────────────
function* refreshAccessPermissions() {
	try {
		const { orgID, siteID: profileSiteID } = yield select((state) => state.Auth.selectedProfile);
		const siteID = profileSiteID;
		const query = siteID !== 'all' ? { siteID } : {};
		const response = yield call(fetchOrgAccess, SuperFetch, query);
		if (response.statusCode === 200) {
			const primary = yield call(fetchPrimaryPermissionGroups);
			yield put(
				accessActions.getAccessPermissionsSuccess({ orgID, siteID, data: { ...response.data, primary } })
			);
		}
	} catch {
		// Silent — no error
	}
}

// ── Grant Permission — API returns 200, socket handles progress ─────────────
function* grantPermission({ payload }) {
	try {
		const response = yield call(() => SuperFetch.post('/access/grantPermission', payload));

		if (response.statusCode === 200) {
			yield put(accessActions.grantPermissionSuccess());
			yield put(accessActions.refreshAccessPermissions());
			Notification('success', 'Permissions granted', 'Permissions have been granted successfully.');
			return;
		}

		const errorMsg = Array.isArray(response.error)
			? response.error.join(', ')
			: response.error || 'Permission denied.';
		Notification('error', 'Grant failed', errorMsg);
		yield put(accessActions.grantPermissionError(errorMsg));
	} catch (error) {
		Notification('error', 'Grant failed', error.message);
		yield put(accessActions.grantPermissionError(error.message));
	}
}

// ── Revoke Permission — API returns 200, socket handles progress ────────────
function* revokePermission({ payload }) {
	try {
		const response = yield call(() => SuperFetch.post('/access/revokePermission', payload));

		if (response.statusCode === 200) {
			yield put(accessActions.revokePermissionSuccess());
			yield put(accessActions.refreshAccessPermissions());
			Notification('success', 'Permissions revoked', 'Permissions have been revoked successfully.');
			return;
		}

		const errorMsg = Array.isArray(response.error)
			? response.error.join(', ')
			: response.error || 'Revocation denied.';
		Notification('error', 'Revoke failed', errorMsg);
		yield put(accessActions.revokePermissionError(errorMsg));
	} catch (error) {
		Notification('error', 'Revoke failed', error.message);
		yield put(accessActions.revokePermissionError(error.message));
	}
}

// ── Revoke by Permission IDs ────────────────────────────────────────────────
function* revokePermissionByIDs({ payload }) {
	try {
		const response = yield call(() => SuperFetch.post('/access/revokePermissionByIDs', payload));

		if (response.statusCode === 200) {
			yield put(accessActions.revokePermissionByIDsSuccess());
			yield put(accessActions.refreshAccessPermissions());
			Notification('success', 'Permissions revoked', 'Permissions have been revoked successfully.');
			return;
		}

		const errorMsg = Array.isArray(response.error)
			? response.error.join(', ')
			: response.error || 'Revocation denied.';
		Notification('error', 'Revoke failed', errorMsg);
		yield put(accessActions.revokePermissionByIDsError(errorMsg));
	} catch (error) {
		Notification('error', 'Revoke failed', error.message);
		yield put(accessActions.revokePermissionByIDsError(error.message));
	}
}

// ── Save Permission Changes — sequential orchestrator ────────────────────────
// Sequences: 1) remove members → 2) revoke perms → 3) add members → 4) grant perms
// Each step waits for the previous to complete before starting.
function* savePermissionChanges({ payload }) {
	const {
		orgID,
		memberRemovals = [], // [{ teamID, personIDs }]
		revokeRules = [], // permission rules to revoke
		memberAdditions = [], // [{ teamID, personIDs }]
		grantRules = [], // permission rules to grant/upsert
		assetRemovals = [], // [{ groupID, assetIDs }] — remove devices from groups
		assetAdditions = [] // [{ groupID, assetIDs }] — add devices to groups
	} = payload;

	try {
		// Step 1: Remove members from teams (must complete before revoke)
		for (const removal of memberRemovals) {
			const res = yield call(() =>
				SuperFetch.delete(`/teams/${removal.teamID}/members`, { personIDs: removal.personIDs })
			);
			if (res.statusCode !== 200) {
				throw new Error(res.error?.message || `Failed to remove people from team ${removal.teamID}`);
			}
		}

		// Step 2: Remove devices from groups
		for (const removal of assetRemovals) {
			const res = yield call(() =>
				SuperFetch.delete(`/deviceGroups/${removal.groupID}/assets`, { assetIDs: removal.assetIDs })
			);
			if (res.statusCode !== 200) {
				throw new Error(res.error?.message || `Failed to remove devices from group ${removal.groupID}`);
			}
		}

		// Step 3: Revoke permissions (must complete before grant)
		if (revokeRules.length > 0) {
			const res = yield call(() => SuperFetch.post('/access/revokePermission', { orgID, rules: revokeRules }));
			if (res.statusCode !== 200) {
				throw new Error(res.error?.message || 'Failed to revoke permissions');
			}
		}

		// Step 4: Add members to teams (must complete before grant — team perms propagate)
		for (const addition of memberAdditions) {
			const res = yield call(() =>
				SuperFetch.post(`/teams/${addition.teamID}/members`, { personIDs: addition.personIDs })
			);
			if (res.statusCode !== 200) {
				throw new Error(res.error?.message || `Failed to add people to team ${addition.teamID}`);
			}
		}

		// Step 5: Add devices to groups
		for (const addition of assetAdditions) {
			const res = yield call(() =>
				SuperFetch.post(`/deviceGroups/${addition.groupID}/assets`, { assetIDs: addition.assetIDs })
			);
			if (res.statusCode !== 200) {
				throw new Error(res.error?.message || `Failed to add devices to group ${addition.groupID}`);
			}
		}

		// Step 6: Grant/upsert permissions
		if (grantRules.length > 0) {
			const res = yield call(() => SuperFetch.post('/access/grantPermission', { orgID, rules: grantRules }));
			if (res.statusCode !== 200) {
				throw new Error(res.error?.message || 'Failed to grant permissions');
			}
		}

		// Refresh data BEFORE marking success — ensures UI has fresh data when modal closes
		const { siteID: refreshSiteID } = yield select((state) => state.Auth.selectedProfile);
		const refreshQuery = refreshSiteID !== 'all' ? { siteID: refreshSiteID } : {};

		// Sync: wait for access refresh (merge the dedicated primary surface so it isn't wiped)
		const accessRes = yield call(fetchOrgAccess, SuperFetch, refreshQuery);
		if (accessRes.statusCode === 200) {
			const primary = yield call(fetchPrimaryPermissionGroups);
			yield put(
				accessActions.getAccessPermissionsSuccess({
					orgID,
					siteID: refreshSiteID,
					data: { ...accessRes.data, primary }
				})
			);
		}

		// Sync: wait for people refresh
		const peopleRes = yield call(() => SuperFetch.get('/people', {}, refreshQuery));
		if (peopleRes.statusCode === 200) {
			yield put(peopleActions.refreshPeopleSuccess({ orgID, siteID: refreshSiteID, data: peopleRes.data }));
		}

		// Sync: wait for teams+members refresh (members must be fresh for selectTeamsByPersonID)
		const teamsRes = yield call(() => SuperFetch.get('/teams/getAllTeamsAllMembers', {}, refreshQuery));
		if (teamsRes.statusCode === 200) {
			yield put(teamsActions.loadAllTeamsMembersSuccess({ orgID, siteID: refreshSiteID, data: teamsRes.data }));
		}

		const hadAssets = assetRemovals.length > 0 || assetAdditions.length > 0;
		const hadMembers = memberRemovals.length > 0 || memberAdditions.length > 0;
		if (hadAssets) {
			const groupsRes = yield call(() => SuperFetch.get('/deviceGroups', {}, refreshQuery));
			if (groupsRes.statusCode === 200) {
				yield put(
					assetGroupActions.refreshGroupsSuccess({ orgID, siteID: refreshSiteID, data: groupsRes.data })
				);
			}
			// Refresh devices-per-group sub-entity so selectDevicesByGroupID updates
			yield put(assetGroupActions.loadAssetsGroups());
			yield put(credentialsActions.refreshCredentials());
		}
		if (hadMembers) {
			// Re-fetch teams+members so selectTeamsByTeamID.members updates
			const teamsRes2 = yield call(() => SuperFetch.get('/teams/getAllTeamsAllMembers', {}, refreshQuery));
			if (teamsRes2.statusCode === 200) {
				yield put(
					teamsActions.loadAllTeamsMembersSuccess({ orgID, siteID: refreshSiteID, data: teamsRes2.data })
				);
			}
		}
		// A revoke changes delivery rows server-side; refetch so Credential Sync reflects it without a reload.
		yield put(credentialsActions.fetchPropagation());
		yield put(accessActions.savePermissionChangesSuccess());
		Notification('success', 'Permissions updated', 'All changes saved successfully.');
	} catch (error) {
		const msg = error.message || 'Permission save failed.';
		Notification('error', 'Save failed', msg);
		yield put(accessActions.savePermissionChangesError(msg));
		// Refresh to get accurate state after partial failure
		yield put(accessActions.refreshAccessPermissions());
		yield put(peopleActions.refreshPeople());
		yield put(teamsActions.refreshTeams());
		yield put(assetGroupActions.refreshGroups());
		yield put(credentialsActions.fetchPropagation());
	}
}

// ── Set Primary Permission Group (Inner Range) ──────────────────────────────
// PUT /access/primary-permission-groups/:personID { assetGroupID }. The server pushes to the IR
// panel and upserts the marker (marker-only, no permission grant); we refresh /access after.
function* setPrimaryPermissionGroup({ payload }) {
	const { personID, assetGroupID } = payload;
	try {
		const response = yield call(() =>
			SuperFetch.put(`/access/primary-permission-groups/${personID}`, { assetGroupID })
		);

		if (response.statusCode === 200) {
			yield put(accessActions.setPrimaryPermissionGroupSuccess());
			yield put(accessActions.refreshAccessPermissions());
			Notification('success', 'Primary group set', 'The primary permission group has been set.');
			return;
		}

		const errorMsg = Array.isArray(response.error)
			? response.error.join(', ')
			: response.error || 'Could not set the primary permission group.';
		Notification('error', 'Set failed', errorMsg);
		yield put(accessActions.setPrimaryPermissionGroupError(errorMsg));
	} catch (error) {
		Notification('error', 'Set failed', error.message);
		yield put(accessActions.setPrimaryPermissionGroupError(error.message));
	}
}

// ── Clear Primary Permission Group (Inner Range) ────────────────────────────
function* clearPrimaryPermissionGroup({ payload }) {
	const { personID } = payload;
	try {
		const response = yield call(() => SuperFetch.delete(`/access/primary-permission-groups/${personID}`));

		if (response.statusCode === 200) {
			yield put(accessActions.clearPrimaryPermissionGroupSuccess());
			yield put(accessActions.refreshAccessPermissions());
			Notification('success', 'Primary group cleared', 'The primary permission group has been cleared.');
			return;
		}

		const errorMsg = Array.isArray(response.error)
			? response.error.join(', ')
			: response.error || 'Could not clear the primary permission group.';
		Notification('error', 'Clear failed', errorMsg);
		yield put(accessActions.clearPrimaryPermissionGroupError(errorMsg));
	} catch (error) {
		Notification('error', 'Clear failed', error.message);
		yield put(accessActions.clearPrimaryPermissionGroupError(error.message));
	}
}

const REQUEST_FAILED = 'The request could not be sent. Try again in a moment.';

// ── Device purchase request ──────────────────────────────────────────────────
// `inquiry` arrives already composed as ONE line (see RequestDevicesModal/inquiry.js).
export function* sendDeviceRequest({ payload }) {
	const { personID, orgID } = yield select((state) => state.Auth.selectedProfile);

	// 🛑 Fail loudly on a missing profile. SuperFetch otherwise resolves the orgID from the
	// ambient store and posts to /v5/undefined/access/inquiryEmail, which 404s silently.
	if (!orgID || !personID) {
		const reason = 'Your profile has not finished loading. Try again in a moment.';
		Notification('error', 'Request not sent', reason);
		yield put(accessActions.sendDeviceRequestError(reason));
		return;
	}

	try {
		// orgID passed EXPLICITLY as the override — never left to the ambient store.
		const response = yield call(() =>
			SuperFetch.post('/access/inquiryEmail', { data: { personID, orgID, inquiry: payload?.inquiry } }, {}, orgID)
		);

		if (response.statusCode === 200) {
			Notification('success', 'Request sent', 'Sent to the Zezamii team by email.');
			yield put(accessActions.sendDeviceRequestSuccess());
			return;
		}

		Notification('error', 'Request not sent', REQUEST_FAILED);
		yield put(accessActions.sendDeviceRequestError(REQUEST_FAILED));
	} catch {
		Notification('error', 'Request not sent', REQUEST_FAILED);
		yield put(accessActions.sendDeviceRequestError(REQUEST_FAILED));
	}
}

// ── Root saga ───────────────────────────────────────────────────────────────
export default function* accessSagas() {
	yield takeLatest(accessActions.getAccessPermissions, getAccessPermissions);
	// Coalesced and single-flight: see accessRefresh.js.
	yield fork(coalesceAccessRefresh, accessActions.refreshAccessPermissions, refreshAccessPermissions);
	yield takeEvery(accessActions.grantPermission, grantPermission);
	yield takeEvery(accessActions.revokePermission, revokePermission);
	yield takeEvery(accessActions.revokePermissionByIDs, revokePermissionByIDs);
	yield takeEvery(accessActions.savePermissionChanges, savePermissionChanges);
	yield takeEvery(accessActions.setPrimaryPermissionGroup, setPrimaryPermissionGroup);
	yield takeEvery(accessActions.clearPrimaryPermissionGroup, clearPrimaryPermissionGroup);
	yield takeEvery(accessActions.sendDeviceRequest, sendDeviceRequest);
}
